import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// The 6 services grid — each card: number, colored square, headline, bullet list, dashed divider, outcome, tag chips
const SQUARE_COLORS = ['#FF553B', '#4CDCE6', '#D0FF3F', '#F5C846', '#FF553B', '#4CDCE6'];

export const Services = () => {
  const { t } = useTranslation();
  const items = t('services.items', { returnObjects: true }) || [];

  return (
    <section id="services" className="relative border-b border-ink py-20 md:py-28" data-testid="services">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-8">
          <span className="font-mono text-xs bg-ink text-page px-2 py-1 tracking-widest2">§ 03</span>
          <span className="font-mono uppercase text-xs tracking-widest2 text-ink">{t('services.eyebrow')}</span>
        </div>

        {/* Heading */}
        <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <h2 className="display text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-ink">
              {t('services.h1_pre')}<br />
              <span className="serif-italic">{t('services.h1_italic')}</span>{' '}
              <span
                className="inline-block px-2 md:px-3 text-ink"
                style={{ background: '#FF553B' }}
              >
                {t('services.h1_hl')}
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pt-6">
            <p className="serif-italic text-xl md:text-2xl leading-snug text-ink/85">
              {t('services.lede')}
            </p>
          </div>
        </div>

        {/* 6-card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((s, i) => {
            const square = SQUARE_COLORS[i % SQUARE_COLORS.length];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="box-line p-6 md:p-7 relative flex flex-col hover:-translate-y-1 transition-transform duration-500"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="display text-5xl md:text-6xl text-ink leading-none">0{i + 1}</span>
                  <span className="w-6 h-6 md:w-7 md:h-7" style={{ background: square, border: '1px solid #0B0B0B' }} />
                </div>
                <h3 className="font-mono uppercase text-sm md:text-base font-medium tracking-tight text-ink mb-3 leading-snug">
                  {s.name}
                </h3>
                <p className="serif-italic text-sm md:text-base text-ink/80 leading-snug mb-5">
                  {s.blurb}
                </p>
                <ul className="space-y-1.5 mb-6 flex-1">
                  {(s.bullets || []).map((b, bi) => (
                    <li key={bi} className="font-mono text-xs md:text-[13px] text-ink/90 flex gap-2">
                      <span aria-hidden className="text-clay shrink-0">▸</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="dash-divider pt-4 mt-auto">
                  <p className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60 mb-1.5">
                    {t('services.outcomeLabel')}
                  </p>
                  <p className="serif-italic text-sm md:text-base text-ink leading-snug mb-4">{s.outcome}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(s.tags || []).map((tag, ti) => (
                      <span key={ti} className="border border-ink/70 px-2 py-1 font-mono uppercase text-[10px] tracking-widest text-ink">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
